const CST_VALIDATION_KEY = 'customer_sign_off';

const PASSING_DOCUMENT_STATUSES = new Set([
  'present',
  'optional_missing',
  'not_required',
  'validated',
  'pass',
]);

const NEEDS_ATTENTION_DOCUMENT_STATUSES = new Set([
  'scope_checklist_not_extracted',
  'requires_manual_review',
  'partial_validation',
  'minor_issues',
  'vmware_discrepancy',
]);

const normalizeLabel = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const isCustomerSignOff = (value) => {
  const normalized = normalizeLabel(value);
  return normalized === 'customersignoff' || normalized === 'customersignofftemplate';
};

const entriesOf = (value) => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value)
    : []
);

const withoutCustomerSignOffEntries = (entries) => Object.fromEntries(
  entriesOf(entries).filter(([key]) => (
    key !== CST_VALIDATION_KEY && !isCustomerSignOff(key)
  )),
);

const displayLabel = (key) => String(key || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const reasonParts = (reason) => String(reason || '').split(' | ').filter(Boolean);

const missingFilesFromReason = (reason) => {
  const match = reasonParts(reason)[0]?.match(/^Required files missing:\s*(.+)$/i);
  return match ? match[1].split(/\s*,\s*/).filter(Boolean) : [];
};

const declaredMissingFiles = (missingFiles, reason) => {
  if (Array.isArray(missingFiles)) return missingFiles;
  if (typeof missingFiles === 'string') {
    return missingFiles.split(/\s*,\s*/).filter(Boolean);
  }
  return missingFilesFromReason(reason);
};

const isMissingValidation = (validation) => {
  const status = String(validation?.status || '').toLowerCase();
  return validation?.applicable !== false && ['missing', 'not_found'].includes(status);
};

const technicalIssue = (key, validation) => {
  if (!validation || validation.applicable === false) return null;
  const status = String(validation.status || '').toLowerCase();
  if (PASSING_DOCUMENT_STATUSES.has(status) || ['missing', 'not_found'].includes(status)) {
    return null;
  }

  const result = NEEDS_ATTENTION_DOCUMENT_STATUSES.has(status)
    ? 'needs_attention'
    : 'fail';
  let reason = validation.reason || validation.parse_error;
  if (!reason && status === 'scope_checklist_not_extracted') {
    reason = 'Scope checklist is present but extraction returned no workstreams; reviewer must inspect it manually.';
  }
  if (!reason) {
    reason = `${displayLabel(key)}: ${status.replace(/_/g, ' ') || 'validation unavailable'}`;
  }
  return { status: status || 'unknown', result, reason };
};

const uniqueLabels = (labels) => {
  const seen = new Set();
  return labels.filter((label) => {
    const key = normalizeLabel(label);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const hasCstText = (value) => /customer sign[- ]?off|\bcst\b/i.test(String(value || ''));

export const projectTechnicalReviewAttachedFiles = (attachedFiles) => {
  if (!attachedFiles || typeof attachedFiles !== 'object' || Array.isArray(attachedFiles)) {
    return {};
  }

  const validationEntries = entriesOf(attachedFiles.validations);
  const sourceMissingFiles = declaredMissingFiles(
    attachedFiles.missing_files,
    attachedFiles.reason,
  );
  const nestedTechnicalMissingFiles = validationEntries
    .filter(([key, validation]) => !isCustomerSignOff(key) && isMissingValidation(validation))
    .map(([key]) => displayLabel(key));
  const technicalMissingFiles = uniqueLabels([
    ...sourceMissingFiles.filter((name) => !isCustomerSignOff(name)),
    ...nestedTechnicalMissingFiles,
  ]);
  const nestedTechnicalIssues = validationEntries
    .filter(([key]) => !isCustomerSignOff(key))
    .map(([key, validation]) => technicalIssue(key, validation))
    .filter(Boolean);
  const cstWasMissing = sourceMissingFiles.some(isCustomerSignOff)
    || validationEntries.some(([key, validation]) => (
      isCustomerSignOff(key) && isMissingValidation(validation)
    ));
  const projected = {
    ...attachedFiles,
    validations: withoutCustomerSignOffEntries(attachedFiles.validations),
    missing_files: technicalMissingFiles,
  };

  delete projected.cst_scope_mismatch_count;
  delete projected.cst_scope_mismatch_workstreams;

  if (attachedFiles.download_summary?.files_by_type) {
    projected.download_summary = {
      ...attachedFiles.download_summary,
      files_by_type: withoutCustomerSignOffEntries(
        attachedFiles.download_summary.files_by_type,
      ),
    };
  }

  if (attachedFiles.status !== 'missing_files' || !cstWasMissing) {
    return projected;
  }

  const suffixes = reasonParts(attachedFiles.reason).slice(1).filter((part) => !hasCstText(part));
  const nestedReasons = nestedTechnicalIssues.map((issue) => issue.reason);
  if (technicalMissingFiles.length > 0) {
    projected.reason = [
      `Required files missing: ${technicalMissingFiles.join(', ')}`,
      ...nestedReasons,
      ...suffixes,
    ].join(' | ');
    return projected;
  }

  const nestedFailure = nestedTechnicalIssues.find((issue) => issue.result === 'fail');
  if (nestedFailure) {
    projected.status = nestedFailure.status;
    projected.result = 'fail';
    projected.reason = [...nestedReasons, ...suffixes].join(' | ');
    return projected;
  }

  const nestedWarning = nestedTechnicalIssues.find((issue) => issue.result === 'needs_attention');
  if (nestedWarning || suffixes.length > 0) {
    projected.status = nestedWarning?.status || (
      suffixes.every((part) => part.startsWith('VMware assessment'))
        ? 'vmware_discrepancy'
        : 'needs_attention'
    );
    projected.result = 'needs_attention';
    projected.reason = [...nestedReasons, ...suffixes].join(' | ');
    return projected;
  }

  projected.status = 'validated';
  projected.result = 'pass';
  projected.reason = 'All required files present';
  return projected;
};
